/* eslint-disable @typescript-eslint/no-unused-vars */
import useMediaQuery from "@mui/material/useMediaQuery";
import bgMultiplier from "assets/bg-multiplier.png";
import classNames from "classnames";
import Button from "components/Buttons/ThemeButton";
import { getClassNameVariant } from "components/DIP/helper";
import { getFlexBasis } from "components/DIP/lib/helper";
import { useStickyDIPAction } from "components/StickyDIP/hooks/useStickyDipAction";
import { useEnvContext } from "contexts";
import { IShopPurchaseOffer } from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppSelector, useImagePath } from "hooks";
import { DipButton } from "interfaces";
import { Fragment } from "react";
import { selectWebStoreState } from "redux/slices";
import { CrossedOutText } from "styles/general_styles";
import { getDisplayRewards } from "helpers/getDisplayRewards";
import { CrownIcon, CrownIconV2 } from "utils/svgIcons";
import {
	BogoItem,
	Container,
	CrossedOutValue,
	CustomLoyaltyPointsV2,
	Footer,
	FreeTag,
	ImageContainer,
	ItemImage,
	LoyaltyPoints,
	LoyaltyValue,
	Multiplier,
	OfferItem,
	OffersContainer,
	Plus,
	RewardAmount,
	RewardItem,
	RewardsContainer
} from "./styles";
import { sortBundleRewards } from "helpers/rewardSort";

interface BogoProps {
	data: Omit<
		IShopPurchaseOffer,
		"onlyNonPayers" | "priority" | "purchaseButtonBannerText" | "bannerText"
	>[];
}

export const Bogo: React.FC<BogoProps> = ({ data }: BogoProps) => {
	const { getImage } = useImagePath("local");
	const { onStickyAction } = useStickyDIPAction();
	const { isFeatureFlagEnabled } = useEnvContext();
	const plus = getImage("/spec_dips/bogo_plus.png");
	const freeTag = getImage("/spec_dips/free_tag.png");
	const isLowWidth = useMediaQuery("(max-width:733px)");
	const isMobile = isLowWidth;
	const { rewards, rewardTypes } = useAppSelector(selectWebStoreState).store;
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const arrayTest = [1, 2, 3, 4, 5, 6];

	if (typeof data === "undefined" || !data) {
		return <Fragment />;
	}
	const bogos = [data[0], data[0]];

	return (
		<Container>
			<OffersContainer id="bs-offers-container">
				{bogos?.map(
					(
						{
							rewardId,
							id,
							rewardMultiplier,
							loyaltyPointReward,
							loyaltyPointCrossout
						},
						index
					) => {
						// prettier-ignore
						const bogoOffer = index + 1 === bogos.length && typeof rewardMultiplier !== "undefined";

						const items = getDisplayRewards(rewardId, {
							rewards,
							rewardTypes
						});
						return (
							<BogoItem key={id + index}>
								<OfferItem
									className={classNames([
										getClassNameVariant(bogos.length as number),
										"item-reward"
									])}
								>
									<RewardsContainer
										className={classNames([
											getClassNameVariant(bogos.length as number),
											"bundle-rewards-item"
										])}
									>
										{sortBundleRewards(items).map(
											({ imageUrl, description }, key) => {
												const flexBasis = getFlexBasis(
													bogos as unknown as DipButton[],
													items,
													isMobile
												);
												const rewardAmount = bogoOffer
													? (
															Number(description?.replace(",", "")) *
															(rewardMultiplier || 0)
													  ).toLocaleString("en-US")
													: description;

												return (
													<RewardItem key={key} width={flexBasis + "%"}>
														<ImageContainer>
															<ItemImage
																className="item-icon"
																src={imageUrl}
																alt=""
															/>
															{bogoOffer && rewardMultiplier > 1 && (
																<Multiplier image={bgMultiplier}>
																	<div>
																		<span>x</span>
																		{rewardMultiplier}
																	</div>
																</Multiplier>
															)}
														</ImageContainer>
														{bogoOffer && rewardMultiplier > 1 && (
															<CrossedOutValue>{description}</CrossedOutValue>
														)}
														<RewardAmount className="description">
															{rewardAmount}
														</RewardAmount>
													</RewardItem>
												);
											}
										)}
										{/* {arrayTest.map(({}, key) => {
											const flexBasis = getFlexBasis(
												bogos as unknown as DipButton[],
												arrayTest,
												isMobile
											);

											return (
												<RewardItem key={key} width={flexBasis + "%"}>
													<ImageContainer>
														<ItemImage
															className="item-icon"
															src={
																"https://cdn.solitairetripeaks.com/WebStore/reward_images/coin.png"
															}
														></ItemImage>
														<Multiplier image={bgMultiplier}>
															<div>
																<span>x</span>
																{3}
															</div>
														</Multiplier>
													</ImageContainer>
													<CrossedOutValue>222</CrossedOutValue>
													<RewardAmount className="description">
														{333}
													</RewardAmount>
												</RewardItem>
											);
										})} */}
									</RewardsContainer>

									<Footer
										className={classNames([
											loyaltyPointReward && "loyalty-reward",
											getClassNameVariant(bogos.length as number)
										])}
									>
										{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
											<CustomLoyaltyPointsV2>
												{loyaltyPointCrossout && (
													<CrossedOutText>
														Earn <CrownIconV2 height={"0.9em"} width={"auto"} />
														{bogoOffer
															? (
																	(Number(loyaltyPointCrossout) || 0) *
																	(rewardMultiplier || 0)
															  ).toLocaleString("en-US")
															: Number(loyaltyPointCrossout)?.toLocaleString(
																	"en-US"
															  )}
													</CrossedOutText>
												)}
												<LoyaltyValue>
													Earn <CrownIconV2 height="1em" width={"auto"} />
													{bogoOffer
														? (
																(loyaltyPointReward || 0) *
																(rewardMultiplier || 0)
														  ).toLocaleString("en-US")
														: loyaltyPointReward?.toLocaleString("en-US")}
												</LoyaltyValue>
											</CustomLoyaltyPointsV2>
										) : (
											<LoyaltyPoints>
												<div>
													Earn <CrownIcon width={16} />
													{bogoOffer
														? (
																(loyaltyPointReward || 0) *
																(rewardMultiplier || 0)
														  ).toLocaleString("en-US")
														: loyaltyPointReward?.toLocaleString("en-US")}
												</div>
											</LoyaltyPoints>
										)}
										{/*
											{isPoisActive && (
												<PoisBountyLabel>
													Contribute to Poi’s Bounty
												</PoisBountyLabel>
											)}
										*/}
									</Footer>
									{bogoOffer && <FreeTag src={freeTag} />}
								</OfferItem>
								{!bogoOffer && <Plus src={plus} />}
							</BogoItem>
						);
					}
				)}
			</OffersContainer>
			<Button
				className="bogo-button"
				onClick={() => {
					onStickyAction(data[0].id, data[0].rewardId, data[0].price);
				}}
			>
				{data[0].price} USD
			</Button>
		</Container>
	);
};
